import React, { useEffect, useState, useCallback } from 'react';
import {
    Table, Button, Modal, Form, Input, Select,
    Space, Typography, message, Upload, Tabs, Tag, Checkbox, Popconfirm, Tooltip,
} from 'antd';
import {
    UploadOutlined, SearchOutlined, ReloadOutlined, DownloadOutlined, SendOutlined,
    DeleteOutlined, FilePdfOutlined, EditOutlined,
} from '@ant-design/icons';
import AdminLayout from '../Layout/AdminLayout';
import axiosInstance from '../../utils/axiosConfig';

const { Text } = Typography;
const { Search } = Input;
const { TextArea } = Input;

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
    const start = CURRENT_YEAR + 1 - i;
    return `${start}-${start + 1}`;
});

const SKIP_REASON_LABELS = {
    no_email: 'Bỏ qua: thí sinh chưa có email',
    no_certificate: 'Bỏ qua: thí sinh chưa có giấy báo trúng tuyển',
    already_sent: 'Bỏ qua: đã gửi trước đó',
};

const AdminAdmissionResults = () => {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filterYear, setFilterYear] = useState(undefined);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });

    const [importOpen, setImportOpen] = useState(false);
    const [importYear, setImportYear] = useState(`${CURRENT_YEAR}-${CURRENT_YEAR + 1}`);
    const [fileList, setFileList] = useState([]);
    const [importing, setImporting] = useState(false);
    const [importSummary, setImportSummary] = useState(null);

    const [imports, setImports] = useState([]);
    const [importsLoading, setImportsLoading] = useState(false);

    const [noticeContent, setNoticeContent] = useState('');
    const [noticeLoading, setNoticeLoading] = useState(false);
    const [noticeSaving, setNoticeSaving] = useState(false);

    const [notifyYear, setNotifyYear] = useState(undefined);
    const [sendingNotify, setSendingNotify] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [notifLoading, setNotifLoading] = useState(false);
    const [notifStatus, setNotifStatus] = useState(undefined);
    const [notifPagination, setNotifPagination] = useState({ current: 1, pageSize: 15, total: 0 });

    const [staticAttachments, setStaticAttachments] = useState([]);
    const [staticAttachmentLoading, setStaticAttachmentLoading] = useState(false);
    const [staticAttachmentUploadingKey, setStaticAttachmentUploadingKey] = useState(null);

    const [certificateUploadingId, setCertificateUploadingId] = useState(null);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [forceResend, setForceResend] = useState(false);
    const [sendingSelected, setSendingSelected] = useState(false);
    const [notifyingRowId, setNotifyingRowId] = useState(null);

    const [emailEditRecord, setEmailEditRecord] = useState(null);
    const [emailEditValue, setEmailEditValue] = useState('');
    const [savingEmail, setSavingEmail] = useState(false);

    const fetchResults = useCallback(async (page = 1, pageSize = 15, search = '', year = undefined) => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/v1/admin/admission-results', {
                params: { page, per_page: pageSize, search: search || undefined, year },
            });
            setResults(res.data?.data ?? []);
            setPagination({
                current: res.data?.meta?.current_page ?? 1,
                pageSize: res.data?.meta?.per_page ?? pageSize,
                total: res.data?.meta?.total ?? 0,
            });
        } catch {
            message.error('Không tải được danh sách trúng tuyển.');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchImports = useCallback(async () => {
        setImportsLoading(true);
        try {
            const res = await axiosInstance.get('/api/v1/admin/admission-results/imports', {
                params: { per_page: 10 },
            });
            setImports(res.data?.data ?? []);
        } catch {
            message.error('Không tải được lịch sử import.');
        } finally {
            setImportsLoading(false);
        }
    }, []);

    const fetchNotice = useCallback(async () => {
        setNoticeLoading(true);
        try {
            const res = await axiosInstance.get('/api/v1/admission-results/notice');
            setNoticeContent(res.data?.data?.content || '');
        } catch {
            message.error('Không tải được nội dung thông báo.');
        } finally {
            setNoticeLoading(false);
        }
    }, []);

    const fetchNotifications = useCallback(async (page = 1, pageSize = 15, status = undefined) => {
        setNotifLoading(true);
        try {
            const res = await axiosInstance.get('/api/v1/admin/admission-results/notifications', {
                params: { page, per_page: pageSize, status, channel: 'email' },
            });
            setNotifications(res.data?.data ?? []);
            setNotifPagination({
                current: res.data?.meta?.current_page ?? 1,
                pageSize: res.data?.meta?.per_page ?? pageSize,
                total: res.data?.meta?.total ?? 0,
            });
        } catch {
            message.error('Không tải được lịch sử gửi thông báo.');
        } finally {
            setNotifLoading(false);
        }
    }, []);

    const fetchStaticAttachments = useCallback(async () => {
        setStaticAttachmentLoading(true);
        try {
            const res = await axiosInstance.get('/api/v1/admin/admission-results/static-attachments');
            setStaticAttachments(res.data?.data ?? []);
        } catch {
            message.error('Không tải được thông tin file đính kèm.');
        } finally {
            setStaticAttachmentLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchResults(1, pagination.pageSize, searchText, filterYear);
        fetchImports();
        fetchNotice();
        fetchNotifications(1, notifPagination.pageSize);
        fetchStaticAttachments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = (value) => {
        setSearchText(value);
        fetchResults(1, pagination.pageSize, value, filterYear);
    };

    const handleYearFilter = (value) => {
        setFilterYear(value);
        fetchResults(1, pagination.pageSize, searchText, value);
    };

    const handleTableChange = (pag) => {
        fetchResults(pag.current, pag.pageSize, searchText, filterYear);
    };

    const handleImport = async () => {
        if (fileList.length === 0) {
            message.error('Vui lòng chọn file CSV.');
            return;
        }
        setImporting(true);
        setImportSummary(null);
        try {
            const formData = new FormData();
            formData.append('file', fileList[0]);
            formData.append('year', importYear);

            const res = await axiosInstance.post('/api/v1/admin/admission-results/import', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 120000,
            });

            setImportSummary(res.data?.data);
            message.success('Import hoàn tất.');
            setFileList([]);
            fetchResults(1, pagination.pageSize, searchText, filterYear);
            fetchImports();
        } catch (err) {
            message.error(err?.response?.data?.message || 'Import thất bại.');
        } finally {
            setImporting(false);
        }
    };

    const handleNotifStatusFilter = (value) => {
        setNotifStatus(value);
        fetchNotifications(1, notifPagination.pageSize, value);
    };

    const handleNotifTableChange = (pag) => {
        fetchNotifications(pag.current, pag.pageSize, notifStatus);
    };

    const handleSendNotify = () => {
        Modal.confirm({
            title: 'Gửi email chúc mừng trúng tuyển',
            content: notifyYear
                ? `Gửi tới tất cả thí sinh có email của năm ${notifyYear} chưa được gửi trước đó. Bạn có chắc chắn?`
                : 'Gửi tới TẤT CẢ thí sinh có email (mọi năm) chưa được gửi trước đó. Bạn có chắc chắn?',
            okText: 'Gửi',
            cancelText: 'Huỷ',
            onOk: async () => {
                setSendingNotify(true);
                try {
                    const res = await axiosInstance.post('/api/v1/admin/admission-results/notify', {
                        year: notifyYear,
                        channel: 'email',
                    });
                    const { sent, skipped, failed } = res.data?.data ?? {};
                    message.success(`Đã gửi xong — Thành công: ${sent}, Bỏ qua: ${skipped}, Lỗi: ${failed}`);
                    fetchNotifications(1, notifPagination.pageSize, notifStatus);
                } catch (err) {
                    message.error(err?.response?.data?.message || 'Gửi thông báo thất bại.');
                } finally {
                    setSendingNotify(false);
                }
            },
        });
    };

    const handleSendSelected = async () => {
        if (!selectedRowKeys.length) return;
        setSendingSelected(true);
        try {
            const res = await axiosInstance.post('/api/v1/admin/admission-results/notify', {
                ids: selectedRowKeys,
                force: forceResend,
            });
            const { sent, skipped, failed } = res.data?.data ?? {};
            message.success(`Đã gửi xong — Thành công: ${sent}, Bỏ qua: ${skipped}, Lỗi: ${failed}`);
            setSelectedRowKeys([]);
            fetchResults(pagination.current, pagination.pageSize, searchText, filterYear);
            fetchNotifications(1, notifPagination.pageSize, notifStatus);
        } catch (err) {
            message.error(err?.response?.data?.message || 'Gửi thông báo thất bại.');
        } finally {
            setSendingSelected(false);
        }
    };

    const handleSendOne = async (record) => {
        setNotifyingRowId(record.id);
        try {
            const res = await axiosInstance.post(`/api/v1/admin/admission-results/${record.id}/notify`, { force: true });
            const outcome = res.data?.data;
            if (outcome?.status === 'sent') {
                message.success(`Đã gửi email cho ${record.full_name}.`);
            } else {
                message.warning(res.data?.message || 'Đã bỏ qua.');
            }
            fetchResults(pagination.current, pagination.pageSize, searchText, filterYear);
            fetchNotifications(1, notifPagination.pageSize, notifStatus);
        } catch (err) {
            message.error(err?.response?.data?.message || 'Gửi email thất bại.');
        } finally {
            setNotifyingRowId(null);
        }
    };

    const handleEditEmail = (record) => {
        setEmailEditRecord(record);
        setEmailEditValue(record.email || '');
    };

    const handleCancelEditEmail = () => {
        setEmailEditRecord(null);
        setEmailEditValue('');
    };

    const handleSaveEmail = async () => {
        if (!emailEditRecord) return;
        const value = emailEditValue.trim();
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            message.error('Email không hợp lệ.');
            return;
        }
        setSavingEmail(true);
        try {
            await axiosInstance.put(`/api/v1/admin/admission-results/${emailEditRecord.id}`, { email: value || null });
            message.success('Đã cập nhật email.');
            setEmailEditRecord(null);
            fetchResults(pagination.current, pagination.pageSize, searchText, filterYear);
        } catch (err) {
            message.error(err?.response?.data?.message || 'Cập nhật email thất bại.');
        } finally {
            setSavingEmail(false);
        }
    };

    const handleUploadCertificate = async (record, file) => {
        setCertificateUploadingId(record.id);
        try {
            const formData = new FormData();
            formData.append('file', file);
            await axiosInstance.post(`/api/v1/admin/admission-results/${record.id}/certificate`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            message.success(`Đã tải lên giấy báo cho ${record.full_name}.`);
            fetchResults(pagination.current, pagination.pageSize, searchText, filterYear);
        } catch (err) {
            message.error(err?.response?.data?.message || 'Tải lên thất bại.');
        } finally {
            setCertificateUploadingId(null);
        }
        return false;
    };

    const handleDeleteCertificate = (record) => {
        Modal.confirm({
            title: 'Xoá giấy báo trúng tuyển',
            content: `Xoá file giấy báo đã upload cho ${record.full_name}?`,
            okText: 'Xoá',
            okType: 'danger',
            cancelText: 'Huỷ',
            onOk: async () => {
                try {
                    await axiosInstance.delete(`/api/v1/admin/admission-results/${record.id}/certificate`);
                    message.success('Đã xoá giấy báo.');
                    fetchResults(pagination.current, pagination.pageSize, searchText, filterYear);
                } catch {
                    message.error('Xoá thất bại.');
                }
            },
        });
    };

    const handleUploadStaticAttachment = async (key, file) => {
        setStaticAttachmentUploadingKey(key);
        try {
            const formData = new FormData();
            formData.append('file', file);
            await axiosInstance.post(`/api/v1/admin/admission-results/static-attachments/${key}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            message.success('Đã tải lên file đính kèm.');
            fetchStaticAttachments();
        } catch (err) {
            message.error(err?.response?.data?.message || 'Tải lên thất bại.');
        } finally {
            setStaticAttachmentUploadingKey(null);
        }
        return false; // ngăn antd Upload tự động gửi request riêng
    };

    const handleDeleteStaticAttachment = (key) => {
        Modal.confirm({
            title: 'Xoá file đính kèm tĩnh',
            content: 'Các email gửi sau thời điểm này sẽ không còn kèm file này nữa. Bạn có chắc chắn?',
            okText: 'Xoá',
            okType: 'danger',
            cancelText: 'Huỷ',
            onOk: async () => {
                try {
                    await axiosInstance.delete(`/api/v1/admin/admission-results/static-attachments/${key}`);
                    message.success('Đã xoá file đính kèm.');
                    fetchStaticAttachments();
                } catch {
                    message.error('Xoá thất bại.');
                }
            },
        });
    };

    const handleSaveNotice = async () => {
        setNoticeSaving(true);
        try {
            await axiosInstance.put('/api/v1/admin/admission-results/notice', { content: noticeContent });
            message.success('Đã lưu nội dung thông báo.');
        } catch {
            message.error('Lưu thất bại.');
        } finally {
            setNoticeSaving(false);
        }
    };

    const columns = [
        { title: 'Họ và tên', dataIndex: 'full_name', width: 200 },
        { title: 'Ngày sinh', dataIndex: 'date_of_birth', width: 110, render: (v) => v || '—' },
        { title: 'CCCD', dataIndex: 'identification', width: 140 },
        {
            title: 'Email',
            dataIndex: 'email',
            width: 240,
            render: (v, record) => (
                <Space size={4} style={{ whiteSpace: 'nowrap' }}>
                    {v ? <Text style={{ whiteSpace: 'nowrap' }}>{v}</Text> : <Tag color="orange">Chưa có email</Tag>}
                    <Button
                        size="small"
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEditEmail(record)}
                    />
                </Space>
            ),
        },
        { title: 'Mã xét tuyển', dataIndex: 'admission_code', width: 130 },
        { title: 'Ngành/chuyên ngành', dataIndex: 'major', ellipsis: true },
        { title: 'Mã sinh viên', dataIndex: 'student_code', width: 130 },
        { title: 'Lớp dự kiến', dataIndex: 'expected_class', width: 110 },
        { title: 'Ngày nhập học', dataIndex: 'enrollment_date', width: 120, render: (v) => v || '—' },
        { title: 'Năm học', dataIndex: 'year', width: 100, align: 'center' },
        {
            title: 'Giấy báo',
            dataIndex: 'certificate_path',
            width: 160,
            render: (v, record) => (v ? (
                <Space size={4}>
                    <Tag icon={<FilePdfOutlined />} color="green">Đã có</Tag>
                    <Button size="small" danger type="text" icon={<DeleteOutlined />} onClick={() => handleDeleteCertificate(record)} />
                </Space>
            ) : (
                <Upload
                    beforeUpload={(file) => handleUploadCertificate(record, file)}
                    accept=".pdf,application/pdf"
                    showUploadList={false}
                    maxCount={1}
                >
                    <Button size="small" icon={<UploadOutlined />} loading={certificateUploadingId === record.id}>Tải lên</Button>
                </Upload>
            )),
        },
        {
            title: 'Gửi email trúng tuyển',
            dataIndex: 'latest_email_notification',
            width: 220,
            render: (notif, record) => {
                const canSend = !!record.email && !!record.certificate_path;
                return (
                    <Space direction="vertical" size={4}>
                        {notif ? (
                            notif.status === 'sent' ? (
                                <Tag color="green">Đã gửi {new Date(notif.sent_at).toLocaleString('vi-VN')}</Tag>
                            ) : notif.status === 'skipped' ? (
                                <Tooltip title={SKIP_REASON_LABELS[notif.error_message] || notif.error_message}>
                                    <Tag color="orange">Đã bỏ qua</Tag>
                                </Tooltip>
                            ) : (
                                <Tooltip title={notif.error_message || 'Không rõ lỗi'}>
                                    <Tag color="red">Gửi lỗi</Tag>
                                </Tooltip>
                            )
                        ) : (
                            <Tag>Chưa gửi</Tag>
                        )}
                        <Popconfirm
                            title={notif ? 'Gửi lại email cho thí sinh này?' : 'Gửi email chúc mừng cho thí sinh này?'}
                            onConfirm={() => handleSendOne(record)}
                            okText="Gửi"
                            cancelText="Huỷ"
                            disabled={!canSend}
                        >
                            <Button size="small" icon={<SendOutlined />} loading={notifyingRowId === record.id} disabled={!canSend}>
                                {notif ? 'Gửi lại' : 'Gửi'}
                            </Button>
                        </Popconfirm>
                        {!canSend && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {!record.email ? 'Thiếu email' : 'Thiếu giấy báo'}
                            </Text>
                        )}
                    </Space>
                );
            },
        },
    ];

    const importColumns = [
        { title: 'Thời gian', dataIndex: 'created_at', width: 170, render: (v) => new Date(v).toLocaleString('vi-VN') },
        { title: 'File', dataIndex: 'original_filename' },
        { title: 'Người import', dataIndex: ['imported_by', 'name'], render: (_, r) => r.imported_by?.name || '—' },
        { title: 'Tổng dòng', dataIndex: 'total_rows', width: 100, align: 'center' },
        { title: 'Thành công', dataIndex: 'success_count', width: 100, align: 'center', render: (v) => <Tag color="green">{v}</Tag> },
        { title: 'Lỗi', dataIndex: 'failed_count', width: 90, align: 'center', render: (v) => v > 0 ? <Tag color="red">{v}</Tag> : <Tag>{v}</Tag> },
    ];

    const notificationColumns = [
        { title: 'Họ và tên', dataIndex: ['admission_result', 'full_name'], render: (_, r) => r.admission_result?.full_name || '—' },
        { title: 'CCCD', dataIndex: ['admission_result', 'identification'], width: 140, render: (_, r) => r.admission_result?.identification || '—' },
        { title: 'Email nhận', dataIndex: 'recipient', width: 220 },
        { title: 'Kênh', dataIndex: 'channel', width: 90, render: (v) => <Tag>{v}</Tag> },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            width: 110,
            render: (v) => {
                if (v === 'sent') return <Tag color="green">Đã gửi</Tag>;
                if (v === 'skipped') return <Tag color="orange">Bỏ qua</Tag>;
                return <Tag color="red">Lỗi</Tag>;
            },
        },
        {
            title: 'Chi tiết lỗi',
            dataIndex: 'error_message',
            ellipsis: true,
            render: (v, r) => (r.status === 'skipped' ? (SKIP_REASON_LABELS[v] || v) : v) || '—',
        },
        {
            title: 'Thời gian gửi',
            dataIndex: 'sent_at',
            width: 170,
            render: (v) => (v ? new Date(v).toLocaleString('vi-VN') : '—'),
        },
    ];

    return (
        <AdminLayout title="Kết quả trúng tuyển">
            <Tabs
                items={[
                    {
                        key: 'list',
                        label: 'Danh sách',
                        children: (
                            <>
                                <Space style={{ marginBottom: 16 }} wrap>
                                    <Select
                                        placeholder="Tất cả các năm"
                                        allowClear
                                        style={{ width: 160 }}
                                        value={filterYear}
                                        onChange={handleYearFilter}
                                        options={YEAR_OPTIONS.map((y) => ({ value: y, label: y }))}
                                    />
                                    <Search
                                        placeholder="Nhập họ tên, CCCD, mã SV, ngành..."
                                        allowClear
                                        enterButton={<SearchOutlined />}
                                        style={{ width: 360 }}
                                        onSearch={handleSearch}
                                    />
                                    <Button icon={<ReloadOutlined />} onClick={() => fetchResults(pagination.current, pagination.pageSize, searchText, filterYear)}>
                                        Làm mới
                                    </Button>
                                    <Button type="primary" icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
                                        Import CSV
                                    </Button>
                                    <Button
                                        icon={<DownloadOutlined />}
                                        href="/templates/mau-import-ket-qua-trung-tuyen.csv"
                                        download="mau-import-ket-qua-trung-tuyen.csv"
                                    >
                                        Tải file mẫu
                                    </Button>
                                    <Text type="secondary">{pagination.total} kết quả</Text>
                                </Space>

                                {selectedRowKeys.length > 0 && (
                                    <Space style={{ marginBottom: 16 }} wrap>
                                        <Text>Đã chọn <b>{selectedRowKeys.length}</b> thí sinh</Text>
                                        <Checkbox checked={forceResend} onChange={(e) => setForceResend(e.target.checked)}>
                                            Gửi lại kể cả đã gửi thành công trước đó
                                        </Checkbox>
                                        <Popconfirm
                                            title="Xác nhận gửi email?"
                                            description={`Gửi email chúc mừng cho ${selectedRowKeys.length} thí sinh đã chọn.`}
                                            onConfirm={handleSendSelected}
                                            okText="Gửi"
                                            cancelText="Huỷ"
                                        >
                                            <Button type="primary" icon={<SendOutlined />} loading={sendingSelected}>
                                                Gửi email đã chọn ({selectedRowKeys.length})
                                            </Button>
                                        </Popconfirm>
                                        <Button onClick={() => setSelectedRowKeys([])}>Bỏ chọn</Button>
                                    </Space>
                                )}

                                <Table
                                    size="small"
                                    columns={columns}
                                    dataSource={results}
                                    rowKey="id"
                                    loading={loading}
                                    rowSelection={{
                                        selectedRowKeys,
                                        onChange: setSelectedRowKeys,
                                    }}
                                    pagination={{
                                        ...pagination,
                                        showTotal: (total, range) => `${range[0]}–${range[1]} / ${total}`,
                                    }}
                                    onChange={handleTableChange}
                                    scroll={{ x: true }}
                                />
                            </>
                        ),
                    },
                    {
                        key: 'imports',
                        label: 'Lịch sử import',
                        children: (
                            <Table
                                size="small"
                                columns={importColumns}
                                dataSource={imports}
                                rowKey="id"
                                loading={importsLoading}
                                pagination={false}
                            />
                        ),
                    },
                    {
                        key: 'notify',
                        label: 'Gửi email trúng tuyển',
                        children: (
                            <>
                                <div style={{
                                    marginBottom: 20, padding: '12px 16px', background: '#fafafa',
                                    border: '1px solid #e8e8e8', borderRadius: 6,
                                }}>
                                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                                        File đính kèm tĩnh (giống nhau cho mọi email — ngoài giấy báo trúng tuyển cá nhân hoá riêng từng thí sinh)
                                    </Text>
                                    {staticAttachmentLoading ? (
                                        <Text type="secondary">Đang tải...</Text>
                                    ) : (
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            {staticAttachments.map(({ key, label, attachment }) => (
                                                <Space key={key} wrap>
                                                    <Text style={{ minWidth: 140, display: 'inline-block' }}>{label}:</Text>
                                                    {attachment ? (
                                                        <>
                                                            <Tag icon={<FilePdfOutlined />} color="blue">{attachment.original_filename}</Tag>
                                                            <Text type="secondary">
                                                                Tải lên bởi {attachment.uploaded_by?.name || '—'} lúc {new Date(attachment.updated_at).toLocaleString('vi-VN')}
                                                            </Text>
                                                            <Upload beforeUpload={(file) => handleUploadStaticAttachment(key, file)} accept=".pdf,application/pdf" showUploadList={false} maxCount={1}>
                                                                <Button size="small" icon={<UploadOutlined />} loading={staticAttachmentUploadingKey === key}>Thay file khác</Button>
                                                            </Upload>
                                                            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteStaticAttachment(key)}>Xoá</Button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Text type="secondary">Chưa có file.</Text>
                                                            <Upload beforeUpload={(file) => handleUploadStaticAttachment(key, file)} accept=".pdf,application/pdf" showUploadList={false} maxCount={1}>
                                                                <Button size="small" icon={<UploadOutlined />} loading={staticAttachmentUploadingKey === key}>Tải lên file PDF</Button>
                                                            </Upload>
                                                        </>
                                                    )}
                                                </Space>
                                            ))}
                                        </Space>
                                    )}
                                </div>

                                <Space style={{ marginBottom: 16 }} wrap align="start">
                                    <Select
                                        placeholder="Tất cả các năm"
                                        allowClear
                                        style={{ width: 160 }}
                                        value={notifyYear}
                                        onChange={setNotifyYear}
                                        options={YEAR_OPTIONS.map((y) => ({ value: y, label: y }))}
                                    />
                                    <Button
                                        type="primary"
                                        icon={<SendOutlined />}
                                        loading={sendingNotify}
                                        onClick={handleSendNotify}
                                    >
                                        Gửi email chúc mừng
                                    </Button>
                                    <Select
                                        placeholder="Tất cả trạng thái"
                                        allowClear
                                        style={{ width: 160 }}
                                        value={notifStatus}
                                        onChange={handleNotifStatusFilter}
                                        options={[
                                            { value: 'sent', label: 'Đã gửi' },
                                            { value: 'failed', label: 'Lỗi' },
                                            { value: 'skipped', label: 'Bỏ qua' },
                                        ]}
                                    />
                                    <Button
                                        icon={<ReloadOutlined />}
                                        onClick={() => fetchNotifications(notifPagination.current, notifPagination.pageSize, notifStatus)}
                                    >
                                        Làm mới
                                    </Button>
                                </Space>
                                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                                    Mỗi email tự động đính kèm file "Giấy báo trúng tuyển" đã tải lên riêng cho từng thí sinh
                                    (cột <Text code>Giấy báo</Text> ở tab Danh sách) và file hướng dẫn nhập học ở trên nếu có.
                                    Thí sinh <b>chưa có giấy báo sẽ tự động bị bỏ qua</b> (không gửi email thiếu file) —
                                    xem số lượng "Bỏ qua" sau khi gửi để biết còn bao nhiêu người chưa có giấy báo.
                                    Chỉ gửi tới thí sinh có email và chưa từng gửi thành công trước đó (chống gửi trùng).
                                    Với danh sách lớn, nên chạy lệnh{' '}
                                    <Text code>php artisan admission-results:notify</Text> trên server thay vì gửi qua giao diện để tránh timeout.
                                </Text>
                                <Table
                                    size="small"
                                    columns={notificationColumns}
                                    dataSource={notifications}
                                    rowKey="id"
                                    loading={notifLoading}
                                    pagination={{
                                        ...notifPagination,
                                        showTotal: (total, range) => `${range[0]}–${range[1]} / ${total}`,
                                    }}
                                    onChange={handleNotifTableChange}
                                    scroll={{ x: true }}
                                />
                            </>
                        ),
                    },
                    {
                        key: 'notice',
                        label: 'Thông báo trang kết quả',
                        children: (
                            <Form layout="vertical" style={{ maxWidth: 720 }}>
                                <Form.Item label="Nội dung box thông tin (HTML) hiển thị ở trang kết quả tra cứu">
                                    <TextArea
                                        rows={8}
                                        value={noticeContent}
                                        onChange={(e) => setNoticeContent(e.target.value)}
                                        disabled={noticeLoading}
                                        placeholder="VD: <p>Thí sinh mang theo CCCD gốc khi làm thủ tục nhập học...</p>"
                                    />
                                </Form.Item>
                                <Button type="primary" loading={noticeSaving} onClick={handleSaveNotice}>
                                    Lưu
                                </Button>
                            </Form>
                        ),
                    },
                ]}
            />

            <Modal
                title="Import danh sách trúng tuyển từ CSV"
                open={importOpen}
                onCancel={() => { setImportOpen(false); setImportSummary(null); setFileList([]); }}
                onOk={handleImport}
                confirmLoading={importing}
                okText="Import"
                cancelText="Đóng"
                destroyOnClose
            >
                <Form layout="vertical">
                    <Form.Item label="Năm học" required>
                        <Select
                            style={{ width: '100%' }}
                            value={importYear}
                            onChange={setImportYear}
                            options={YEAR_OPTIONS.map((y) => ({ value: y, label: y }))}
                        />
                    </Form.Item>
                    <Form.Item label="File CSV" required>
                        <Upload
                            beforeUpload={(file) => { setFileList([file]); return false; }}
                            onRemove={() => setFileList([])}
                            fileList={fileList.map((f, i) => ({ uid: String(i), name: f.name }))}
                            accept=".csv,text/csv"
                            maxCount={1}
                        >
                            <Button icon={<UploadOutlined />}>Chọn file CSV</Button>
                        </Upload>
                        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                            File cần đúng 2 dòng tiêu đề: dòng 1 là tên cột mô tả bằng tiếng Việt (chỉ để đọc cho dễ),
                            dòng 2 là tên cột kỹ thuật khớp với cơ sở dữ liệu — <b>không được sửa dòng 2</b>
                            (full_name, date_of_birth, identification, phone, email, admission_code, major, student_code,
                            expected_class, enrollment_time, enrollment_date, location, year). Thiếu cột bắt buộc (full_name, identification)
                            ở dòng 2 sẽ bị từ chối ngay. Số điện thoại và Email không bắt buộc nhưng cần có để gửi
                            email/Zalo thông báo sau này. Chạy lại cùng file sẽ cập nhật (không tạo trùng) theo cặp CCCD + năm học.
                            Chưa có sẵn dữ liệu?{' '}
                            <a href="/templates/mau-import-ket-qua-trung-tuyen.csv" download="mau-import-ket-qua-trung-tuyen.csv">
                                Tải file mẫu
                            </a>.
                        </Text>
                    </Form.Item>

                    {importSummary && (
                        <div style={{ marginTop: 8 }}>
                            <Text>Tổng: {importSummary.total_rows} — Thành công: {importSummary.success_count} — Lỗi: {importSummary.failed_count}</Text>
                            {importSummary.errors?.length > 0 && (
                                <ul style={{ maxHeight: 150, overflowY: 'auto', color: '#c0392b', marginTop: 8 }}>
                                    {importSummary.errors.map((err, idx) => <li key={idx}>{err}</li>)}
                                </ul>
                            )}
                        </div>
                    )}
                </Form>
            </Modal>

            <Modal
                title={emailEditRecord ? `Sửa email — ${emailEditRecord.full_name}` : 'Sửa email'}
                open={!!emailEditRecord}
                onCancel={handleCancelEditEmail}
                onOk={handleSaveEmail}
                confirmLoading={savingEmail}
                okText="Lưu"
                cancelText="Huỷ"
                destroyOnClose
            >
                <Form layout="vertical">
                    <Form.Item label="Email">
                        <Input
                            value={emailEditValue}
                            onChange={(e) => setEmailEditValue(e.target.value)}
                            onPressEnter={handleSaveEmail}
                            placeholder="email@example.com"
                            autoFocus
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </AdminLayout>
    );
};

export default AdminAdmissionResults;
